import { z } from 'zod'
import { listResponseSchema } from '@/core/types/ListResponse.ts'
import { responseSchema } from '@/core/types/Response.ts'

export type RentabilityInitialState = {
  selected: Rentability | null
  openCreateModal: boolean
}

export const ReconnectionTasaSchema = z.object({
  id: z.string().optional(),
  days: z.number().nonnegative({ message: 'Los días no debe ser negativo' }),
  penaltyDelay: z
    .number()
    .nonnegative({ message: 'La tasa no debe ser negativo' })
    .multipleOf(0.01, {
      message: 'La tasa debe tener como máximo dos decimales'
    })
})

export type ReconnectionTasa = z.infer<typeof ReconnectionTasaSchema>

export const ReconnectionTasaResponseSchema = responseSchema(
  ReconnectionTasaSchema
)

export type ReconnectionTasaResponse = z.infer<
  typeof ReconnectionTasaResponseSchema
>

export const resetPasswordBaseSchema = z.object({
  days: z.number().nonnegative({ message: 'Los días no debe ser negativo' }),
  penaltyDelay: z
    .number()
    .nonnegative({ message: 'La tasa no debe ser negativo' })
    .multipleOf(0.01, {
      message: 'La tasa debe tener como máximo dos decimales'
    })
})

export const resetPasswordSchema = resetPasswordBaseSchema.merge(
  z.object({
    id: z.string().optional(),
    active: z.boolean()
  })
)
export type ResetPasswordForm = z.infer<typeof resetPasswordBaseSchema>

export const resetPasswordListResponseSchema =
  listResponseSchema(resetPasswordSchema)
export type ResetPasswordListResponse = z.infer<
  typeof resetPasswordListResponseSchema
>
export type ResetPasswordDto = ResetPasswordForm

export const rentabilityTypeSchema = z.enum([
  'PRODUCT',
  'MISCELLANEOUS',
  'MOTO'
])

export type RentabilityType = z.infer<typeof rentabilityTypeSchema>

export const RENTABILITY_TYPE_LABELS: Record<RentabilityType, string> = {
  PRODUCT: 'Producto',
  MISCELLANEOUS: 'Miscelánea',
  MOTO: 'Moto'
}

export const RENTABILITY_TYPE_OPTIONS: {
  id: RentabilityType
  label: string
}[] = (Object.keys(RENTABILITY_TYPE_LABELS) as RentabilityType[]).map((id) => ({
  id,
  label: RENTABILITY_TYPE_LABELS[id]
}))

export const rentabilitySchema = z.object({
  id: z.string().optional(),
  quotes: z.string(),
  exchange: z.string(),
  timesQuotes: z.string(),
  isMain: z.boolean(),
  type: z.array(rentabilityTypeSchema)
})

export type Rentability = z.infer<typeof rentabilitySchema>

export const RentabilityResponseSchema = listResponseSchema(rentabilitySchema)

export type RentabilityResponse = z.infer<typeof RentabilityResponseSchema>

export const RentabilityFormSchema = z.object({
  id: z.string().optional(),
  quotes: z
    .number()
    .nonnegative({ message: 'El número de cuotas no debe ser negativa' }),
  exchange: z
    .number()
    .nonnegative({ message: 'La tasa no debe ser negativa' })
    .multipleOf(0.01, {
      message: 'La tasa debe tener como máximo dos decimales'
    }),
  timesQuotes: z
    .number()
    .nonnegative({ message: 'El tiempo no debe ser negativo' }),
  type: z.array(rentabilityTypeSchema).min(1, { message: 'Requerido' })
})

export const CurrencySchema = z.object({
  id: z.string().optional(),
  code: z.enum(['BS', 'USD']),
  exchangeBuy: z.number(),
  exchangeSell: z.number(),
  mainCurrency: z.boolean()
})

export type Currency = z.infer<typeof CurrencySchema>

export const CurrencyResponseSchema = responseSchema(CurrencySchema)

export type CurrencyResponse = z.infer<typeof CurrencyResponseSchema>

export const CurrencyListResponseSchema = listResponseSchema(CurrencySchema)

export type CurrencyListResponse = z.infer<typeof CurrencyListResponseSchema>

export const CurrencyFormSchema = z.object({
  id: z.string().optional(),
  code: z.string().min(1, { message: 'El código es requerido' }),
  exchangeBuy: z
    .number({
      required_error: 'El tipo de cambio de compra es requerido',
      invalid_type_error: 'El tipo de cambio de compra debe ser un número'
    })
    .nonnegative({
      message: 'El tipo de cambio de compra no debe ser negativo'
    })
    .multipleOf(0.01, {
      message: 'El tipo de cambio debe tener como máximo dos decimales'
    }),
  exchangeSell: z
    .number({
      required_error: 'El tipo de cambio de venta es requerido',
      invalid_type_error: 'El tipo de cambio de venta debe ser un número'
    })
    .nonnegative({
      message: 'El tipo de cambio de venta no debe ser negativo'
    })
    .multipleOf(0.01, {
      message: 'El tipo de cambio debe tener como máximo dos decimales'
    }),
  mainCurrency: z.boolean().default(false)
})

export type CurrencyForm = z.infer<typeof CurrencyFormSchema>
